/**
 * Parent Quick Actions FAB (Floating Action Button)
 *
 * A floating button that provides quick access to common parent actions,
 * most importantly the QuickRead feature for each child.
 */

import { useState } from 'react';
import {
  ActionIcon,
  Menu,
  Loader,
  Divider,
  Box
} from '@mantine/core';
import {
  IconPlus,
  IconPlayerPlay,
  IconBook,
  IconCards,
  IconUser,
  IconSparkles,
  IconMicrophone,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { parentApi } from '@/features/parent/api/parentApi';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useParentQuickRead } from '@/features/recommendations';
import { useRecommendAssignments } from '@/features/parent/hooks/useParent';
import { capitalizeName } from '@/utils/name';

interface ChildQuickReadItemProps {
  childId: string;
  childName: string;
  onClose: () => void;
}

function ChildQuickReadItem({ childId, childName, onClose }: ChildQuickReadItemProps) {
  const { startQuickRead, isDisabled } = useParentQuickRead({
    studentId: childId,
    childName,
  });

  return (
    <Menu.Item
      leftSection={<IconPlayerPlay size={14} />}
      onClick={() => {
        startQuickRead();
        onClose();
      }}
      disabled={isDisabled}
      color="teal"
    >
      Read with {childName}
    </Menu.Item>
  );
}

export function ParentQuickActionsFAB() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const [opened, setOpened] = useState(false);
  const recommendAssignments = useRecommendAssignments();

  const isParent = user?.roles?.includes('parent');
  const isOnParentRoute = location.pathname.startsWith('/parent') ||
    location.pathname.startsWith('/mobile/parent');

  const { data: children, isLoading } = useQuery({
    queryKey: ['children', user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id && isParent && isOnParentRoute,
  });

  // Only show FAB for parents on parent routes
  if (!isParent || !isOnParentRoute) {
    return null;
  }

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: 'calc(70px + env(safe-area-inset-bottom))',
        right: '16px',
        zIndex: 200,
      }}
    >
      <Menu
        opened={opened}
        onChange={setOpened}
        position="top-end"
        offset={8}
        withArrow
        arrowPosition="center"
      >
        <Menu.Target>
          <ActionIcon
            size={56}
            radius="xl"
            variant="gradient"
            gradient={{ from: 'orange', to: 'pink', deg: 90 }}
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <IconPlus
              size={28}
              style={{
                transition: 'transform 0.2s',
                transform: opened ? 'rotate(45deg)' : 'rotate(0deg)',
              }}
            />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {isLoading ? (
            <Menu.Item disabled>
              <Loader size="xs" /> Loading...
            </Menu.Item>
          ) : children && children.length > 0 ? (
            <>
              <Menu.Label>Quick Read</Menu.Label>
              {children.map((child) => (
                <ChildQuickReadItem
                  key={child.id}
                  childId={child.id}
                  childName={capitalizeName(child.name)}
                  onClose={() => setOpened(false)}
                />
              ))}
              <Divider my="xs" />
            </>
          ) : (
            <>
              <Menu.Item disabled leftSection={<IconUser size={14} />}>
                No children added yet
              </Menu.Item>
              <Divider my="xs" />
            </>
          )}

          {children && children.some((c: any) => c.fluency_enabled) && (
            <>
              <Menu.Label>Read Aloud</Menu.Label>
              {children
                .filter((c: any) => c.fluency_enabled)
                .map((child: any) => (
                  <Menu.Item
                    key={`fluency-${child.id}`}
                    leftSection={<IconMicrophone size={14} />}
                    color="grape"
                    onClick={() => {
                      navigate(`/parent/fluency/start?studentId=${child.id}`);
                      setOpened(false);
                    }}
                  >
                    {capitalizeName(child.name)}
                  </Menu.Item>
                ))}
              <Divider my="xs" />
            </>
          )}

          {children && children.length > 0 && (
            <>
              <Menu.Label>Smart Pick</Menu.Label>
              {children.map((child) => (
                <Menu.Item
                  key={`smart-${child.id}`}
                  leftSection={<IconSparkles size={14} />}
                  onClick={() => {
                    recommendAssignments.mutate(child.id);
                    setOpened(false);
                  }}
                  disabled={recommendAssignments.isPending}
                >
                  Assign for {capitalizeName(child.name)}
                </Menu.Item>
              ))}
              <Divider my="xs" />
            </>
          )}

          <Menu.Label>Quick Actions</Menu.Label>
          <Menu.Item
            leftSection={<IconBook size={14} />}
            onClick={() => {
              navigate('/mobile/parent/assign/story');
              setOpened(false);
            }}
          >
            Assign Story
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCards size={14} />}
            onClick={() => {
              navigate('/mobile/parent/assign/flashcards');
              setOpened(false);
            }}
          >
            Assign Flashcards
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
